// src/composables/useUrlFilters.ts
import { computed, ref, ComputedRef } from 'vue';
import type IUrl from '@/interfaces/IUrl';
import { useUrlPanel } from '@/composables/useUrlPanel';

export function useUrlFilters(filterFn: (url: IUrl) => boolean) {
    // Use the existing composable for fetch-status filtering.
    // We rename the returned "filteredUrls" to "baseUrls".
    const {
        filteredUrls: baseUrls,
        activeFetchStatusFilters,
        toggleFetchStatusFilter,
        fetchStatusCounts
    } = useUrlPanel(filterFn);

    // Sitemap filtering state and logic.
    const activeSitemapFilters = ref<boolean[]>([]);
    function toggleSitemapFilter(value: boolean) {
        const index = activeSitemapFilters.value.indexOf(value);
        if (index === -1) {
            activeSitemapFilters.value.push(value);
        } else {
            activeSitemapFilters.value.splice(index, 1);
        }
    }

    // Compute counts based on the base URLs.
    const sitemapCount: ComputedRef<number> = computed(() =>
        baseUrls.value.filter(url => url.isSitemap).length
    );
    const nonSitemapCount: ComputedRef<number> = computed(() =>
        baseUrls.value.filter(url => !url.isSitemap).length
    );

    // Compute the final list of URLs to display.
    const displayedUrls: ComputedRef<IUrl[]> = computed<IUrl[]>(() => {
        if (activeSitemapFilters.value.length === 0) {
            return baseUrls.value;
        }
        return baseUrls.value.filter(url =>
            activeSitemapFilters.value.includes(url.isSitemap)
        );
    });

    return {
        // Final (displayed) URLs after all filtering.
        displayedUrls,
        // Fetch-status filtering properties.
        activeFetchStatusFilters,
        toggleFetchStatusFilter,
        fetchStatusCounts,
        // Sitemap filtering properties.
        activeSitemapFilters,
        toggleSitemapFilter,
        sitemapCount,
        nonSitemapCount
    };
}
